import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash-es';
import I18n from 'utils/I18n';
import Modal, { ModalActions, ModalBody, ModalTitle } from '@strava/ui/Modal';
import Button from '@strava/ui/Button';
import Tooltip from '@strava/ui/Tooltip';
import NavigationHelpNormalXsmall from '@strava/icons/NavigationHelpNormalXsmall';

import { Markup } from 'interweave';
import styles from './EmbedModal.scss';

// quick and dirty RadioGroup that supports non-string values
const RadioGroup = ({ options, value, onChange, name }) => {
  return options.map(([label, v]) => (
    <React.Fragment key={v}>
      <input
        type="radio"
        checked={v === value}
        onChange={() => onChange(v)}
        id={String(v)}
        name={name}
      />
      <label htmlFor={String(v)}>{label}</label>{' '}
    </React.Fragment>
  ));
};

const EmbedModal = ({ isOpen, onDismiss, embedType, embedId, embedCode }) => {
  const [isCodeCopied, setIsCodeCopied] = useState(false);
  const codeCopyDisplayTimeout = useRef();
  const staticEmbedParams = { embedType, embedId };
  const [configurableEmbedParams, setConfigurableEmbedParams] = useState({
    units: null,
    hideElevation: null
  });

  const updateParams = (update) =>
    setConfigurableEmbedParams((p) => ({ ...p, ...update }));

  const toggleNullParam = (param) =>
    setConfigurableEmbedParams((p) =>
      p[param] ? { ...p, [param]: null } : { ...p, [param]: true }
    );

  const placeholderAttrString = Object.entries({
    ...staticEmbedParams,
    ...configurableEmbedParams
  })
    .filter(([, v]) => v !== null)
    .map(([k, v]) => `data-${kebabCase(k)}="${encodeURIComponent(v)}"`)
    .join(' ');

  const embedCodeDisplay =
    embedCode ??
    `<div class="strava-embed-placeholder" ${placeholderAttrString}></div><script src="https://strava-embeds.com/embed.js"></script>`;

  function copyCode() {
    navigator.clipboard.writeText(embedCodeDisplay).then(() => {
      setIsCodeCopied(true);
      // if the user clicks the copy button before the previous timeout fires,
      // make sure to clean that up so that we can reset the clock.
      // this is a noop if "current" is null or expired
      clearTimeout(codeCopyDisplayTimeout.current);
      codeCopyDisplayTimeout.current = setTimeout(() => {
        setIsCodeCopied(false);
      }, 4000);
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      hasClose={true}
      aria-labelledby="embed-modal-title"
      className={styles.embedModal}
    >
      <ModalTitle id="embed-modal-title">
        {I18n.t('templates.share.embed.get_code')}
      </ModalTitle>
      <ModalBody>
        {embedType === 'route' && (
          <RouteEmbedConfiguration
            embedType={embedType}
            params={configurableEmbedParams}
            updateParams={updateParams}
            toggleNullParam={toggleNullParam}
          />
        )}
        <div className={styles.embedCodeContainer}>{embedCodeDisplay}</div>
      </ModalBody>
      <ModalActions>
        <Button onClick={onDismiss}>
          {I18n.t('components.button_cta_with_cancel.cancel')}
        </Button>
        <Button variant="primary" onClick={copyCode}>
          {isCodeCopied
            ? I18n.t('templates.share.embed.code_copied')
            : I18n.t('templates.share.embed.copy_code')}
        </Button>
      </ModalActions>
      <ModalBody className={styles.legalTerms}>
        <Markup
          content={I18n.t('templates.share.embed.by_embedding_html', {
            terms_path: 'https://www.strava.com/legal/api'
          })}
        />
      </ModalBody>
    </Modal>
  );
};

const RouteEmbedConfiguration = ({ params, updateParams, toggleNullParam }) => {
  // the "Auto" units option has a special tooltip with help text
  const autoUnitsLabel = (
    <span>
      {I18n.t('templates.share.embed.units_options.auto')}{' '}
      <Tooltip
        label={I18n.t('templates.share.embed.auto_units_tooltip')}
        className={styles.autoTooltip}
        position="right"
      >
        <NavigationHelpNormalXsmall size={12} style={{ opacity: 0.8 }} />
      </Tooltip>
    </span>
  );

  return (
    <>
      <div className={styles.embedOption}>
        <input
          type="checkbox"
          checked={params.hideElevation === null}
          id="ele-toggle"
          onChange={() => toggleNullParam('hideElevation')}
        />{' '}
        <label htmlFor="ele-toggle">
          {I18n.t('templates.share.embed.show_elevation_profile')}
        </label>
      </div>
      <div className={styles.embedOption}>
        <span className={styles.bold}>
          {I18n.t('templates.share.embed.units_label')}
        </span>{' '}
        <RadioGroup
          options={[
            [autoUnitsLabel, null],
            [I18n.t('templates.share.embed.units_options.metric'), 'metric'],
            [I18n.t('templates.share.embed.units_options.imperial'), 'imperial']
          ]}
          value={params.units}
          onChange={(units) => updateParams({ units })}
        />
      </div>
    </>
  );
};

RouteEmbedConfiguration.propTypes = {
  params: PropTypes.shape({
    units: PropTypes.oneOf([null, 'metric', 'imperial']),
    hideElevation: PropTypes.oneOf([null, true])
  }).isRequired,
  updateParams: PropTypes.func.isRequired,
  toggleNullParam: PropTypes.func.isRequired
};

EmbedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  embedType: PropTypes.string.isRequired,
  embedId: PropTypes.string.isRequired,
  embedCode: PropTypes.string
};

EmbedModal.defaultProps = {
  embedCode: null
};

export default EmbedModal;
